import { Injectable } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ApiService } from "@app/_core/api/api.service";
import NavigableRoutes from "@app/_core/enums/navigable-routes";
import { Subject } from "rxjs";
import { HttpResponse } from "../api/http-response.interface";
import { HttpStatus } from "../api/http-status.enum";
import { AppRoutes } from "../constants/app-routes.constant";
import StorageHelper from "../helpers/storage.helper";
import { NotyService } from "./../helpers/zorro-notification.helper";
import { ILoginPayload, IRefreshTokenBody, IRefreshTokenPayload } from "./account-api.interface";
import { AccountApi } from "./account.api";
import { ISupplierDTO } from "./current-supplier.interface";
import { CurrentUser } from "./current-user.interface";
import { CurrentUserService } from "./current-user.service";
import { UserRole } from "./user-role";

@Injectable({ providedIn: "root" })
export class AccountManager {
	$refreshTokenFailed = new Subject<void>();

	constructor(
		private accountApi: AccountApi,
		private router: Router,
		private currentUserService: CurrentUserService,
		private route: ActivatedRoute,
		private notificationService: NotyService,
		private apiService: ApiService,
	) {}

	async login(username: string, password: string): Promise<HttpResponse<null>> {
		const payload: ILoginPayload = { username, password };

		let response: HttpResponse<CurrentUser>;
		if (StorageHelper.getToken()) {
			response = await this.accountApi.getUserDetails();
		} else {
			response = await this.accountApi.login(payload);
		}

		if (response.status === HttpStatus.SUCCESS) {
			StorageHelper.saveToken(response.data.token);
			StorageHelper.saveRefreshToken(response.data.refreshToken);

			this.currentUserService.setCurrentUser(response.data);
			if (this.currentUserService.isNonAdmin()) {
				const supplierResponse: HttpResponse<ISupplierDTO> = await this.accountApi.getCurrentSupplierById(response.data.supplierId);
				if (supplierResponse.data) {
					this.currentUserService.setSupplier(supplierResponse.data);
				}
			}
			const returnUrl = this.route.snapshot.queryParams.returnUrl as string;
			if (returnUrl) {
				this.router.navigateByUrl(returnUrl);
			} else {
				this.navigateBasedOnUserRole(response.data.role);
			}
		} else if (response.status !== HttpStatus.ACCOUNT_NOT_ACTIVE) {
			const customerLoginResponse: HttpResponse<CurrentUser> = await this.accountApi.customerLogin(payload);
			if (customerLoginResponse.status === HttpStatus.SUCCESS) {
				StorageHelper.saveToken(customerLoginResponse.data.token);
				StorageHelper.saveRefreshToken(customerLoginResponse.data.refreshToken);
				StorageHelper.saveIsOrgillVendorAccount("true");
				customerLoginResponse.data.role = UserRole.CUSTOMER;
				this.currentUserService.setCurrentUser(customerLoginResponse.data);
				this.router.navigateByUrl(NavigableRoutes.ManageItems);
				return { status: customerLoginResponse.status, data: null };
			}
		}

		return { status: response.status, data: null, responseMessage: response.responseMessage };
	}

	async getUserDetails(): Promise<HttpResponse<null>> {
		const userDetailsResponse: HttpResponse<CurrentUser> = await this.accountApi.getUserDetails();
		if (userDetailsResponse.status === HttpStatus.SUCCESS) {
			this.currentUserService.setCurrentUser(userDetailsResponse.data);
			if (this.currentUserService.isNonAdmin()) {
				const supplierResponse: HttpResponse<ISupplierDTO> = await this.accountApi.getCurrentSupplierById(userDetailsResponse.data.supplierId);
				if (supplierResponse.data) {
					this.currentUserService.setSupplier(supplierResponse.data);
				}
			}
		}

		if (userDetailsResponse.status !== HttpStatus.SUCCESS) {
			StorageHelper.clearPrimaryInfo();
			StorageHelper.killSession();
			this.router.navigateByUrl("account/sign-in");
		}

		return { status: userDetailsResponse.status, data: null };
	}

	async remoteLogin(token: string): Promise<HttpResponse<HttpStatus>> {
		const response = await this.accountApi.remoteLogin(token);

		if (response.status !== HttpStatus.SUCCESS) {
			this.notificationService.httpError(response.status);
			this.router.navigateByUrl(NavigableRoutes.Login);
			return null;
		}

		this.currentUserService.setCurrentUser(response.data);
		this.router.navigateByUrl(NavigableRoutes.ManageItems);
		return { status: response.status, data: null };
	}

	async refreshToken(): Promise<HttpResponse<null>> {
		const refreshTokenPayload: IRefreshTokenPayload = {
			refreshToken: StorageHelper.getRefreshToken(),
		};

		let refreshTokenResponse: HttpResponse<IRefreshTokenBody>;

		if (StorageHelper.getIsOrgillVendorAccount() === "true") {
			refreshTokenResponse = await this.accountApi.refreshCustomerToken(refreshTokenPayload);
		} else {
			refreshTokenResponse = await this.accountApi.refreshToken(refreshTokenPayload);
		}

		if (refreshTokenResponse.status !== HttpStatus.SUCCESS) {
			StorageHelper.killSession();
			this.currentUserService.resetCurrentUser();
			this.$refreshTokenFailed.next();
			this.router.navigate([NavigableRoutes.Login]);
		} else {
			StorageHelper.saveToken(refreshTokenResponse.data.token);
			StorageHelper.saveRefreshToken(refreshTokenResponse.data.refreshToken);
		}

		return { status: refreshTokenResponse.status, data: null };
	}

	navigateBasedOnUserRole(userRole: UserRole): void {
		switch (userRole) {
			case UserRole.SUPER_ADMIN:
				this.router.navigate([AppRoutes.SUPPLIER_INFO]);
				break;
			case UserRole.ADMIN:
				this.router.navigate([AppRoutes.SUPPLIER_INFO]);
				break;
			case UserRole.PRIMARY:
				if (!this.currentUserService.getSupplier().hasProfileCompleted) {
					this.router.navigate([AppRoutes.ACCOUNT_DATA_PROFILE], { queryParams: { profileNotCompleted: true } });
				} else {
					this.router.navigate([AppRoutes.MANAGE_ITEMS]);
				}
				break;
			case UserRole.CUSTOMER:
				this.router.navigate([AppRoutes.CUSTOMER_VIEW]);
				break;
			default:
				this.router.navigate([AppRoutes.BASE]);
				break;
		}
	}

	signOut(canRedirectToLogin = true): void {
		if (!canRedirectToLogin) {
			StorageHelper.killSession();
			this.currentUserService.resetCurrentUser();
		}
		if (canRedirectToLogin) {
			this.apiService.tearDown();
			this.router.navigateByUrl(NavigableRoutes.Login).then((res) => {
				if (res) {
					StorageHelper.killSession();
					this.currentUserService.resetCurrentUser();
				}
			});
		}
	}
}
